import { BadgePercent, Edit, Trash2, Link as LinkIcon } from "lucide-react";
import { Button } from "@/shared/ui/button";
import { Badge } from "@/shared/ui/badge";
import { TaxListItem as TaxListItemType, formatTaxValue, formatTaxDate, getTaxTypeLabel } from "./utils";

interface TaxListItemProps {
  item: TaxListItemType;
  onEdit: (item: TaxListItemType) => void;
  onDelete: (item: TaxListItemType) => void;
  disabled?: boolean;
}

export function TaxListItem({ item, onEdit, onDelete, disabled }: TaxListItemProps) {
  const tax = item.tax;
  const createdLabel =
    item.kind === "existing"
      ? `Registrada em ${formatTaxDate(tax.createdAt)}`
      : `Criada em ${formatTaxDate(tax.createdAt)} • Aguarda envio`;

  return (
    <li key={`${item.kind}-${tax.id}`} className="rounded-md border bg-muted/30 p-4">
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div className="flex flex-1 gap-3">
          <div className="mt-1 text-muted-foreground">
            <BadgePercent className="h-5 w-5" />
          </div>
          <div className="min-w-0 space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="secondary">{getTaxTypeLabel(tax.type)}</Badge>
              <Badge variant="outline">{tax.currency}</Badge>
              <span className="text-xs text-muted-foreground">{createdLabel}</span>
              {item.kind === "pending" && (
                <Badge variant="outline" className="border-amber-200 bg-amber-50 text-amber-700">
                  Pendente
                </Badge>
              )}
            </div>
            <p className="font-mono text-sm text-muted-foreground">
              {formatTaxValue(tax.currency, tax.value)}
            </p>
            {tax.description ? (
              <p className="break-words text-sm text-muted-foreground">{tax.description}</p>
            ) : (
              <p className="text-sm italic text-muted-foreground">Sem descrição</p>
            )}
            {tax.document && (
              <a
                href={tax.document}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
              >
                <LinkIcon className="h-4 w-4" />
                Comprovante
              </a>
            )}
            {item.kind === "pending" && tax.documents && tax.documents.length > 0 && (
              <p className="text-xs text-muted-foreground">
                {tax.documents.length} documento(s) aguardando envio
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1 self-start md:self-center">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => onEdit(item)}
            disabled={disabled}
            title="Editar taxa"
          >
            <Edit className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="text-destructive hover:text-destructive"
            onClick={() => onDelete(item)}
            disabled={disabled}
            title="Remover taxa"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </li>
  );
}

